import FileDownload from "@mui/icons-material/FileDownload";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Collapse,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  Select,
  SelectChangeEvent,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  DateRangePicker,
  LocalizationProvider,
  MultiInputDateRangeField,
} from "@mui/x-date-pickers-pro";
import { DateRange } from "@mui/x-date-pickers-pro/models";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { useAlerts } from "components/Alerts/AlertProvider";
import { DateShortcutChips } from "components/DateShortcutChips";
import { parseOmnisearch, stringifyOmnisearch } from "components/Omnisearch/FilterPopover";
import { STATE_NAMES } from "encamp-shared/src/constants/states";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import {
  RegulatoryStatusOption,
  SubmissionMaterializationType,
  SubmissionStatus,
  SubmissionType,
} from "generated-graphql/graphql";
import { ExportParams, useExportData } from "hooks/useExportData";
import { DateTime } from "luxon";
import { useOmnisearch } from "providers/OmnisearchDataContext";
import { OmnisearchProvider } from "providers/OmnisearchProvider";
import { type SyntheticEvent, useCallback, useMemo, useState } from "react";
import { Link as RouterLink, Outlet, useMatch } from "react-router-dom";
import { getDateRangeShortcuts } from "util/dateRangeShortcuts";

import { useBreadcrumb } from "../../../hooks/useBreadcrumbs";
import { ADDITIONAL_FILTER_COLUMNS, COLUMNS, EXCLUDE_FILTER_COLUMNS } from "./columns";
import { GridSection } from "./GridSection";
import { StatsProvider, useStats } from "./StatsContext";
import { SummaryOverview } from "./Summary/SummaryOverview";
import { mergeOmnisearchWithDefaultsPreservingFreeText } from "./utils";

const STATUS_FILTER_OPTIONS: Record<SubmissionStatus, string> = {
  [SubmissionStatus.NotStarted]: "🔶 Upcoming",
  [SubmissionStatus.InProgress]: "🚧 In Progress",
  [SubmissionStatus.EncampInitialReview]: "⛺️ In Encamp Review",
  [SubmissionStatus.InReview]: "🔍 In Review",
  [SubmissionStatus.ProcessingSubmission]: "✅ Awaiting Submission",
  [SubmissionStatus.FilingComplete]: "🎉 Submitted",
  [SubmissionStatus.NotReporting]: "⭕️ Not Reporting",
};

const ALL_STATUSES = Object.keys(STATUS_FILTER_OPTIONS) as SubmissionStatus[];

const DEFAULT_STATUSES = ALL_STATUSES;

const US_STATES = Object.keys(STATE_NAMES);

type StateFilterOption = { kind: "state"; value: string } | { kind: "toggleAll" };

const STATE_TOGGLE_ALL_OPTION: StateFilterOption = {
  kind: "toggleAll",
};

function buildStateFilterOption(value: string): StateFilterOption {
  return { kind: "state", value };
}

const STATE_FILTER_OPTIONS: StateFilterOption[] = [
  STATE_TOGGLE_ALL_OPTION,
  ...US_STATES.map(buildStateFilterOption),
];

function getStateName(state: string): string {
  return Object.entries(STATE_NAMES).find(([abbreviation]) => abbreviation === state)?.[1] ?? state;
}

function getStateFilterOptionLabel(option: StateFilterOption): string {
  return option.kind === "toggleAll"
    ? "Show/Hide All"
    : `${getStateName(option.value)} (${option.value})`;
}

const ALL_SUBMISSION_TYPES = Object.values(SubmissionType);

type SubmissionTypeFilterOption =
  | { kind: "submissionType"; value: SubmissionType }
  | { kind: "toggleAll" };

const SUBMISSION_TYPE_TOGGLE_ALL_OPTION: SubmissionTypeFilterOption = {
  kind: "toggleAll",
};

function buildSubmissionTypeFilterOption(value: SubmissionType): SubmissionTypeFilterOption {
  return { kind: "submissionType", value };
}

const SUBMISSION_TYPE_FILTER_OPTIONS: SubmissionTypeFilterOption[] = [
  SUBMISSION_TYPE_TOGGLE_ALL_OPTION,
  ...ALL_SUBMISSION_TYPES.map(buildSubmissionTypeFilterOption),
];

function isSubmissionType(value: string): value is SubmissionType {
  return ALL_SUBMISSION_TYPES.some((submissionType) => submissionType === value);
}

function getSubmissionTypeFilterOptionLabel(option: SubmissionTypeFilterOption): string {
  return option.kind === "toggleAll" ? "Show/Hide All" : prettyPrintEnumValue(option.value);
}

const GENERATOR_STATUS_OPTIONS: {
  label: string;
  value: RegulatoryStatusOption;
}[] = [
  { label: "LQG", value: RegulatoryStatusOption.LargeQuantityGenerator },
  { label: "SQG", value: RegulatoryStatusOption.SmallQuantityGenerator },
  { label: "VSQG", value: RegulatoryStatusOption.VerySmallQuantityGenerator },
  { label: "None", value: RegulatoryStatusOption.None },
];

const ALL_GENERATOR_STATUSES = [
  RegulatoryStatusOption.LargeQuantityGenerator,
  RegulatoryStatusOption.SmallQuantityGenerator,
  RegulatoryStatusOption.VerySmallQuantityGenerator,
  RegulatoryStatusOption.None,
];

// Forward-looking shortcuts for the due-date filter.
const datePickerShortcuts = getDateRangeShortcuts("FUTURE");

function extractOmnisearchValue(omnisearch: string | null, key: string): string | undefined {
  if (!omnisearch) return undefined;
  const regex = new RegExp(`(?:^|\\s)${key}:(?:"([^"]+)"|([^\\s]+))`, "i");
  const match = omnisearch.match(regex);
  return match ? (match[1] ?? match[2]) : undefined;
}

function arraysEqual<T>(a: T[], b: T[]): boolean {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((val, idx) => val === sortedB[idx]);
}

function WasteFulfillmentDashboardContent() {
  const theme = useTheme();
  const backgroundColor = theme.palette.canvas;
  const isOverviewRoute = useMatch("/staff/fulfillment/waste");

  const { statsOpen, handleToggle } = useStats();

  const {
    omnisearch,
    editOmnisearchKeyValues: editKeyValues,
    removeOmnisearchKeyValue: removeKey,
    setOmnisearchImmediate,
  } = useOmnisearch();

  const defaultDateRange = useMemo(() => {
    const startOfToday = DateTime.now().startOf("day");
    const endOfNinetyDays = startOfToday.plus({ days: 90 }).endOf("day");
    return {
      startDateTime: startOfToday,
      endDateTime: endOfNinetyDays,
    };
  }, []);

  const includeOverdueValue = extractOmnisearchValue(omnisearch, "includeOverdue");
  const includeOverdue = useMemo(() => {
    return includeOverdueValue !== "false";
  }, [includeOverdueValue]);

  const overdueFloor = useMemo(() => {
    return DateTime.now().minus({ months: 3 }).startOf("day");
  }, []);
  const overdueCutoffDate = useMemo(() => {
    return overdueFloor.toFormat("MMM d, yyyy");
  }, [overdueFloor]);

  const handleIncludeOverdueChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const checked = event.target.checked;
      if (checked) {
        removeKey("includeOverdue");
      } else {
        editKeyValues([["includeOverdue", "false"]]);
      }
    },
    [editKeyValues, removeKey]
  );

  const onlyOverdueValue = extractOmnisearchValue(omnisearch, "onlyOverdue");
  const onlyOverdue = useMemo(() => {
    return onlyOverdueValue === "true";
  }, [onlyOverdueValue]);

  const materializationTypeValue = extractOmnisearchValue(omnisearch, "materializationType");
  const materializationTypes = useMemo(() => {
    if (!materializationTypeValue) return undefined;
    const types = materializationTypeValue
      .split("|")
      .filter((t): t is SubmissionMaterializationType =>
        Object.values(SubmissionMaterializationType).includes(t as SubmissionMaterializationType)
      );
    return types.length > 0 ? types : undefined;
  }, [materializationTypeValue]);

  type StaffViewByValue =
    | "all"
    | "assignedToMe"
    | "queuedForMe"
    | "needsMyAttention"
    | "peerReviewQueue";

  const assignedToMeValue = extractOmnisearchValue(omnisearch, "assignedToMe");
  const assignedToMe = assignedToMeValue === "true";

  const queuedForMeValue = extractOmnisearchValue(omnisearch, "queuedForMe");
  const queuedForMe = queuedForMeValue === "true";

  const needsMyAttentionValue = extractOmnisearchValue(omnisearch, "needsMyAttention");
  const needsMyAttention = needsMyAttentionValue === "true";

  const peerReviewQueueValue = extractOmnisearchValue(omnisearch, "peerReviewQueue");
  const peerReviewQueue = peerReviewQueueValue === "true";

  const staffViewByValue = useMemo((): StaffViewByValue => {
    if (assignedToMe) return "assignedToMe";
    if (queuedForMe) return "queuedForMe";
    if (needsMyAttention) return "needsMyAttention";
    if (peerReviewQueue) return "peerReviewQueue";
    return "all";
  }, [assignedToMe, queuedForMe, needsMyAttention, peerReviewQueue]);

  const handleStaffViewByChange = useCallback(
    (event: SelectChangeEvent<StaffViewByValue>) => {
      const value = event.target.value as StaffViewByValue;

      let cleaned = omnisearch
        .replace(/\b(assignedToMe|queuedForMe|needsMyAttention|peerReviewQueue):\S+/g, "")
        .replace(/\s+/g, " ")
        .trim();

      switch (value) {
        case "assignedToMe":
          cleaned = `${cleaned} assignedToMe:true`.trim();
          break;
        case "queuedForMe":
          cleaned = `${cleaned} queuedForMe:true`.trim();
          break;
        case "needsMyAttention":
          cleaned = `${cleaned} needsMyAttention:true`.trim();
          break;
        case "peerReviewQueue":
          cleaned = `${cleaned} peerReviewQueue:true`.trim();
          break;
      }

      setOmnisearchImmediate(cleaned);
    },
    [omnisearch, setOmnisearchImmediate]
  );

  const [statusOpen, setStatusOpen] = useState(false);
  const statusFilterValue = extractOmnisearchValue(omnisearch, "status");

  const committedStatuses = useMemo(() => {
    if (statusFilterValue === undefined || statusFilterValue === "") {
      return [...DEFAULT_STATUSES];
    }
    if (statusFilterValue === "none") {
      return [];
    }
    const parsedStatuses = statusFilterValue
      .split(/[|,]/)
      .filter((s) => s.length > 0 && ALL_STATUSES.includes(s as SubmissionStatus));
    return parsedStatuses.length > 0 ? parsedStatuses : [...DEFAULT_STATUSES];
  }, [statusFilterValue]);

  const [intermediateStatuses, setIntermediateStatuses] = useState<string[]>([]);
  const selectedStatuses = statusOpen ? intermediateStatuses : committedStatuses;

  const handleStatusClose = useCallback(() => {
    setStatusOpen(false);
    if (intermediateStatuses.length === 0) {
      editKeyValues([["status", "none"]]);
    } else if (arraysEqual([...intermediateStatuses].sort(), [...DEFAULT_STATUSES].sort())) {
      removeKey("status");
    } else {
      editKeyValues([["status", intermediateStatuses.join("|")]]);
    }
  }, [intermediateStatuses, editKeyValues, removeKey]);

  const handleStatusOpen = useCallback(() => {
    setIntermediateStatuses([...committedStatuses]);
    setStatusOpen(true);
  }, [committedStatuses]);

  const handleStatusesChange = useCallback((e: SelectChangeEvent<string[]>) => {
    const raw = e.target.value as string[];
    if (raw.some((v) => v === null || v === undefined)) {
      return;
    }
    const values = raw.filter(
      (v): v is string =>
        v !== null &&
        v !== undefined &&
        v !== "all" &&
        typeof v === "string" &&
        ALL_STATUSES.includes(v as SubmissionStatus)
    );
    setIntermediateStatuses(values);
  }, []);

  const handleToggleAllStatuses = useCallback(() => {
    if (intermediateStatuses.length === ALL_STATUSES.length) {
      setIntermediateStatuses([]);
    } else {
      setIntermediateStatuses([...ALL_STATUSES]);
    }
  }, [intermediateStatuses.length]);

  const handleResetStatuses = useCallback(() => {
    setIntermediateStatuses([...DEFAULT_STATUSES]);
  }, []);

  const isStatusFilterAtDefault = useMemo(() => {
    const statuses = statusOpen ? intermediateStatuses : committedStatuses;
    if (statuses.length !== DEFAULT_STATUSES.length) return false;
    return DEFAULT_STATUSES.every((s) => statuses.includes(s));
  }, [statusOpen, intermediateStatuses, committedStatuses]);

  const [stateOpen, setStateOpen] = useState(false);
  const statesFilterValue = extractOmnisearchValue(omnisearch, "state");

  const committedStates = useMemo(() => {
    if (statesFilterValue === undefined || statesFilterValue === "") {
      return [...US_STATES];
    }
    if (statesFilterValue === "none") {
      return [];
    }
    return statesFilterValue.split(/[|,]/).filter((s) => s.length > 0);
  }, [statesFilterValue]);

  const [intermediateStates, setIntermediateStates] = useState<string[]>([]);
  const selectedStates = stateOpen ? intermediateStates : committedStates;
  const selectedStateOptions = useMemo(
    () =>
      STATE_FILTER_OPTIONS.filter(
        (option) => option.kind === "state" && selectedStates.includes(option.value)
      ),
    [selectedStates]
  );

  const handleStateClose = useCallback(() => {
    setStateOpen(false);
    if (intermediateStates.length === 0) {
      editKeyValues([["state", "none"]]);
    } else if (intermediateStates.length === US_STATES.length) {
      removeKey("state");
    } else {
      editKeyValues([["state", intermediateStates.join("|")]]);
    }
  }, [intermediateStates, editKeyValues, removeKey]);

  const handleStateOpen = useCallback(() => {
    setIntermediateStates([...committedStates]);
    setStateOpen(true);
  }, [committedStates]);

  const handleStatesChange = useCallback((_event: SyntheticEvent, options: StateFilterOption[]) => {
    if (options.some((option) => option.kind === "toggleAll")) {
      setIntermediateStates((current) =>
        current.length === US_STATES.length ? [] : [...US_STATES]
      );
      return;
    }

    setIntermediateStates(
      options.flatMap((option) => (option.kind === "state" ? [option.value] : []))
    );
  }, []);

  const handleResetStates = useCallback(() => {
    setIntermediateStates([...US_STATES]);
  }, []);

  const isStateFilterAtDefault = selectedStates.length === US_STATES.length;

  const [generatorStatusOpen, setGeneratorStatusOpen] = useState(false);
  const generatorStatusFilterValue = extractOmnisearchValue(omnisearch, "generatorStatus");

  const committedGeneratorStatuses = useMemo(() => {
    if (generatorStatusFilterValue === undefined || generatorStatusFilterValue === "") {
      return [...ALL_GENERATOR_STATUSES];
    }
    if (generatorStatusFilterValue === "none") {
      return [RegulatoryStatusOption.None];
    }
    const parsedStatuses = generatorStatusFilterValue
      .split(/[|,]/)
      .filter(
        (s) => s.length > 0 && ALL_GENERATOR_STATUSES.includes(s as RegulatoryStatusOption)
      ) as RegulatoryStatusOption[];
    return parsedStatuses.length > 0 ? parsedStatuses : [...ALL_GENERATOR_STATUSES];
  }, [generatorStatusFilterValue]);

  const [intermediateGeneratorStatuses, setIntermediateGeneratorStatuses] = useState<
    RegulatoryStatusOption[]
  >([]);
  const selectedGeneratorStatuses = generatorStatusOpen
    ? intermediateGeneratorStatuses
    : committedGeneratorStatuses;

  const handleGeneratorStatusClose = useCallback(() => {
    setGeneratorStatusOpen(false);
    if (intermediateGeneratorStatuses.length === 0) {
      editKeyValues([["generatorStatus", RegulatoryStatusOption.None]]);
    } else if (intermediateGeneratorStatuses.length === ALL_GENERATOR_STATUSES.length) {
      removeKey("generatorStatus");
    } else {
      editKeyValues([["generatorStatus", intermediateGeneratorStatuses.join("|")]]);
    }
  }, [intermediateGeneratorStatuses, editKeyValues, removeKey]);

  const handleGeneratorStatusOpen = useCallback(() => {
    setIntermediateGeneratorStatuses([...committedGeneratorStatuses]);
    setGeneratorStatusOpen(true);
  }, [committedGeneratorStatuses]);

  const handleGeneratorStatusChange = useCallback((e: SelectChangeEvent<string[]>) => {
    const raw = e.target.value as string[];
    if (raw.some((v) => v === null || v === undefined)) {
      return;
    }
    const values = raw.filter(
      (v) =>
        v !== "select-all" &&
        v !== null &&
        v !== undefined &&
        ALL_GENERATOR_STATUSES.includes(v as RegulatoryStatusOption)
    ) as RegulatoryStatusOption[];
    setIntermediateGeneratorStatuses(values);
  }, []);

  const handleToggleAllGeneratorStatuses = useCallback(() => {
    if (intermediateGeneratorStatuses.length === ALL_GENERATOR_STATUSES.length) {
      setIntermediateGeneratorStatuses([]);
    } else {
      setIntermediateGeneratorStatuses([...ALL_GENERATOR_STATUSES]);
    }
  }, [intermediateGeneratorStatuses.length]);

  const handleResetGeneratorStatuses = useCallback(() => {
    setIntermediateGeneratorStatuses([...ALL_GENERATOR_STATUSES]);
  }, []);

  const isGeneratorStatusFilterAtDefault =
    selectedGeneratorStatuses.length === ALL_GENERATOR_STATUSES.length;

  const [submissionTypeOpen, setSubmissionTypeOpen] = useState(false);
  const submissionTypeFilterValue = extractOmnisearchValue(omnisearch, "submissionType");

  const committedSubmissionTypes = useMemo(() => {
    if (submissionTypeFilterValue === undefined || submissionTypeFilterValue === "") {
      return [...ALL_SUBMISSION_TYPES];
    }
    if (submissionTypeFilterValue === "none") {
      return [];
    }
    const parsed = submissionTypeFilterValue
      .split(/[|,]/)
      .filter((value) => value.length > 0)
      .filter(isSubmissionType);
    return parsed.length > 0 ? parsed : [...ALL_SUBMISSION_TYPES];
  }, [submissionTypeFilterValue]);

  const [intermediateSubmissionTypes, setIntermediateSubmissionTypes] = useState<SubmissionType[]>(
    []
  );
  const selectedSubmissionTypes = submissionTypeOpen
    ? intermediateSubmissionTypes
    : committedSubmissionTypes;
  const selectedSubmissionTypeOptions = useMemo(
    () =>
      SUBMISSION_TYPE_FILTER_OPTIONS.filter(
        (option) =>
          option.kind === "submissionType" && selectedSubmissionTypes.includes(option.value)
      ),
    [selectedSubmissionTypes]
  );

  const handleSubmissionTypeClose = useCallback(() => {
    setSubmissionTypeOpen(false);
    if (intermediateSubmissionTypes.length === 0) {
      editKeyValues([["submissionType", "none"]]);
    } else if (intermediateSubmissionTypes.length === ALL_SUBMISSION_TYPES.length) {
      removeKey("submissionType");
    } else {
      editKeyValues([["submissionType", intermediateSubmissionTypes.join("|")]]);
    }
  }, [intermediateSubmissionTypes, editKeyValues, removeKey]);

  const handleSubmissionTypeOpen = useCallback(() => {
    setIntermediateSubmissionTypes([...committedSubmissionTypes]);
    setSubmissionTypeOpen(true);
  }, [committedSubmissionTypes]);

  const handleSubmissionTypeChange = useCallback(
    (_event: SyntheticEvent, options: SubmissionTypeFilterOption[]) => {
      if (options.some((option) => option.kind === "toggleAll")) {
        setIntermediateSubmissionTypes((current) =>
          current.length === ALL_SUBMISSION_TYPES.length ? [] : [...ALL_SUBMISSION_TYPES]
        );
        return;
      }

      setIntermediateSubmissionTypes(
        options.flatMap((option) => (option.kind === "submissionType" ? [option.value] : []))
      );
    },
    []
  );

  const handleResetSubmissionTypes = useCallback(() => {
    setIntermediateSubmissionTypes([...ALL_SUBMISSION_TYPES]);
  }, []);

  const isSubmissionTypeFilterAtDefault =
    selectedSubmissionTypes.length === ALL_SUBMISSION_TYPES.length;

  const defaultDueDateRange = useMemo(() => {
    const defaultStart = defaultDateRange.startDateTime.toISODate() ?? "";
    const defaultEnd = defaultDateRange.endDateTime.toISODate() ?? "";
    return `${defaultStart}..${defaultEnd}`;
  }, [defaultDateRange]);

  const dueDateValue = extractOmnisearchValue(omnisearch, "dueDate");
  const [dueDateStart, dueDateEnd] = useMemo(() => {
    if (!dueDateValue) {
      return [defaultDateRange.startDateTime, defaultDateRange.endDateTime];
    }
    const parts = dueDateValue.split("..");
    const start = parts[0] ? DateTime.fromISO(parts[0], { zone: "utc" }) : null;
    const end = parts[1] ? DateTime.fromISO(parts[1], { zone: "utc" }) : null;
    return [start, end];
  }, [dueDateValue, defaultDateRange]);

  // Include Overdue is a no-op once the user's explicit Due Start is on or
  // before the rolling overdue floor (today − 3mo) — their window already
  // covers anything the toggle would have pulled in. Disable it in that case
  // so the affordance doesn't suggest it has any effect.
  const isOverdueToggleRedundant = useMemo(() => {
    return dueDateStart != null && dueDateStart <= overdueFloor;
  }, [dueDateStart, overdueFloor]);

  const isDueDateFilterAtDefault = useMemo(() => {
    if (!dueDateStart || !dueDateEnd) return false;
    return (
      dueDateStart.toISODate() === defaultDateRange.startDateTime.toISODate() &&
      dueDateEnd.toISODate() === defaultDateRange.endDateTime.toISODate()
    );
  }, [dueDateStart, dueDateEnd, defaultDateRange]);

  const handleDueDateRangeChange = useCallback(
    (newValue: DateRange<DateTime>) => {
      const [start, end] = newValue;
      if (!start && !end) {
        if (!dueDateValue) return;
        removeKey("dueDate");
        return;
      }

      const startStr = start?.toISODate() ?? "";
      const endStr = end?.toISODate() ?? "";
      const nextDueDateValue = `${startStr}..${endStr}`;

      if (nextDueDateValue === defaultDueDateRange) {
        if (!dueDateValue) return;
        removeKey("dueDate");
      } else if (nextDueDateValue !== dueDateValue) {
        editKeyValues([["dueDate", nextDueDateValue]]);
      } else {
        return;
      }
    },
    [defaultDueDateRange, dueDateValue, editKeyValues, removeKey]
  );

  const areFiltersAtDefault = useMemo(() => {
    return (
      isStatusFilterAtDefault &&
      isStateFilterAtDefault &&
      isGeneratorStatusFilterAtDefault &&
      isSubmissionTypeFilterAtDefault &&
      isDueDateFilterAtDefault &&
      !assignedToMe &&
      !queuedForMe &&
      !needsMyAttention &&
      !peerReviewQueue &&
      includeOverdue &&
      !onlyOverdue
    );
  }, [
    isStatusFilterAtDefault,
    isStateFilterAtDefault,
    isGeneratorStatusFilterAtDefault,
    isSubmissionTypeFilterAtDefault,
    isDueDateFilterAtDefault,
    assignedToMe,
    queuedForMe,
    needsMyAttention,
    peerReviewQueue,
    includeOverdue,
    onlyOverdue,
  ]);

  const handleResetFilters = useCallback(() => {
    const keysToRemove = new Set([
      "status",
      "dueDate",
      "state",
      "generatorStatus",
      "submissionType",
      "assignedToMe",
      "queuedForMe",
      "needsMyAttention",
      "peerReviewQueue",
      "includeOverdue",
      "onlyOverdue",
    ]);

    const tokens: string[] = [];
    let current = "";
    let inQuotes = false;
    let quoteChar = "";
    for (let i = 0; i < omnisearch.length; i++) {
      const char = omnisearch[i];
      if (!inQuotes && (char === '"' || char === "'")) {
        inQuotes = true;
        quoteChar = char;
        current += char;
      } else if (inQuotes && char === quoteChar) {
        inQuotes = false;
        quoteChar = "";
        current += char;
      } else if (!inQuotes && char === " ") {
        if (current.trim()) {
          tokens.push(current.trim());
          current = "";
        }
      } else {
        current += char;
      }
    }
    if (current.trim()) tokens.push(current.trim());

    const filteredTokens = tokens.filter((token) => {
      const colonIndex = token.indexOf(":");
      if (colonIndex > 0) {
        const key = token.substring(0, colonIndex);
        return !keysToRemove.has(key);
      }
      return true;
    });

    setOmnisearchImmediate(filteredTokens.join(" "));
  }, [omnisearch, setOmnisearchImmediate]);

  const defaultSearch = useMemo(() => {
    const parts: string[] = [];
    parts.push(`status:${DEFAULT_STATUSES.join("|")}`);
    parts.push(`dueDate:${defaultDueDateRange}`);
    return parts.join(" ");
  }, [defaultDueDateRange]);

  const effectiveSearch = useMemo((): string | null => {
    return mergeOmnisearchWithDefaultsPreservingFreeText({
      omnisearch,
      defaultSearch,
      parseOmnisearch,
      stringifyOmnisearch,
    });
  }, [omnisearch, defaultSearch]);

  useBreadcrumb({
    label: "Waste Fulfillment",
    to: "/staff/fulfillment/waste",
  });

  const alert = useAlerts();
  const { startExport, status: exportStatus } = useExportData({
    autoDownload: true,
    timeoutInSeconds: 300,
  });

  const handleExportCsv = useCallback(() => {
    const params: ExportParams = {
      exportType: "wasteSubmissionsForStaff",
      fileName: "waste-submissions-staff",
      filter: {
        search: effectiveSearch ?? undefined,
        sort: [{ field: "deadline", sort: "asc" }],
        includeOverdue,
        onlyOverdue,
        materializationTypes: materializationTypes ?? undefined,
      },
    };
    alert.info("Export started; download will open when ready.");
    startExport(params);
  }, [startExport, alert, effectiveSearch, includeOverdue, onlyOverdue, materializationTypes]);

  const exportLoadingOrPolling = exportStatus === "loading";

  return (
    <Stack sx={{ background: backgroundColor }}>
      {isOverviewRoute && (
        <Stack
          paddingX={2}
          paddingY={1.5}
          sx={{
            backgroundColor: "background.paper",
            borderBottom: "1px solid",
            borderBottomColor: "divider",
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            gap={1.5}
            flexWrap="wrap"
          >
            <Stack direction="row" gap={1.5} alignItems="flex-end" flexWrap="wrap">
              {/* Status Filter */}
              <FormControl variant="standard" size="small" sx={{ width: 140 }}>
                <InputLabel id="status-filter-label">Status</InputLabel>
                <Select
                  labelId="status-filter-label"
                  open={statusOpen}
                  onOpen={handleStatusOpen}
                  onClose={handleStatusClose}
                  multiple
                  value={selectedStatuses}
                  onChange={handleStatusesChange}
                  label="Status"
                  variant="standard"
                  inputProps={{
                    autoComplete: "off",
                    "data-lpignore": "true",
                    "data-form-type": "other",
                    "data-1p-ignore": "true",
                  }}
                  sx={{
                    "& .MuiSelect-select": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    },
                  }}
                  renderValue={(selected) => {
                    if (selected.length === ALL_STATUSES.length) {
                      return `All (${selected.length})`;
                    }
                    if (selected.length <= 2) {
                      return (selected as string[])
                        .map((s) => STATUS_FILTER_OPTIONS[s as SubmissionStatus] || s)
                        .join(", ");
                    }
                    return `${selected.length} selected`;
                  }}
                  MenuProps={{
                    MenuListProps: {
                      autoFocusItem: selectedStatuses.length === ALL_STATUSES.length ? false : true,
                    },
                    PaperProps: {
                      sx: { minWidth: 280 },
                    },
                  }}
                >
                  <Box
                    sx={{
                      pl: 2,
                      pr: 1,
                      py: 0.75,
                      borderBottom: 1,
                      borderColor: "divider",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedStatuses.length === ALL_STATUSES.length}
                          indeterminate={
                            selectedStatuses.length > 0 &&
                            selectedStatuses.length < ALL_STATUSES.length
                          }
                          onChange={handleToggleAllStatuses}
                        />
                      }
                      label="Show/Hide All"
                      sx={{ margin: 0 }}
                    />
                    {!isStatusFilterAtDefault && (
                      <Button onClick={handleResetStatuses}>Reset</Button>
                    )}
                  </Box>
                  {Object.entries(STATUS_FILTER_OPTIONS).map(([key, label]) => (
                    <MenuItem key={key} value={key}>
                      <Checkbox checked={selectedStatuses.includes(key)} />
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Generator Status Filter */}
              <FormControl variant="standard" size="small" sx={{ width: 140 }}>
                <InputLabel id="generator-status-filter-label">Gen Status</InputLabel>
                <Select
                  labelId="generator-status-filter-label"
                  open={generatorStatusOpen}
                  onOpen={handleGeneratorStatusOpen}
                  onClose={handleGeneratorStatusClose}
                  multiple
                  value={selectedGeneratorStatuses}
                  onChange={handleGeneratorStatusChange}
                  label="Gen Status"
                  variant="standard"
                  inputProps={{
                    autoComplete: "off",
                    "data-lpignore": "true",
                    "data-form-type": "other",
                    "data-1p-ignore": "true",
                  }}
                  sx={{
                    "& .MuiSelect-select": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    },
                  }}
                  renderValue={(selected) => {
                    if (selected.length === ALL_GENERATOR_STATUSES.length) {
                      return `All (${selected.length})`;
                    }
                    return selected
                      .map((s) => GENERATOR_STATUS_OPTIONS.find((o) => o.value === s)?.label ?? s)
                      .join(", ");
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: { minWidth: 280 },
                    },
                  }}
                >
                  <Box
                    sx={{
                      pl: 2,
                      pr: 1,
                      py: 0.75,
                      borderBottom: 1,
                      borderColor: "divider",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      minWidth: 240,
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={
                            selectedGeneratorStatuses.length === ALL_GENERATOR_STATUSES.length
                          }
                          indeterminate={
                            selectedGeneratorStatuses.length > 0 &&
                            selectedGeneratorStatuses.length < ALL_GENERATOR_STATUSES.length
                          }
                          onChange={handleToggleAllGeneratorStatuses}
                        />
                      }
                      label="Show/Hide All"
                      sx={{ margin: 0 }}
                    />
                    {!isGeneratorStatusFilterAtDefault && (
                      <Button onClick={handleResetGeneratorStatuses}>Reset</Button>
                    )}
                  </Box>
                  {GENERATOR_STATUS_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Checkbox checked={selectedGeneratorStatuses.includes(option.value)} />
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Submission Type Filter */}
              <Autocomplete
                multiple
                disableClearable
                disableCloseOnSelect
                openOnFocus
                size="small"
                sx={{
                  width: 150,
                  "& .MuiAutocomplete-inputRoot": {
                    flexWrap: "nowrap",
                  },
                  "& .MuiAutocomplete-input": {
                    minWidth: 0,
                  },
                }}
                open={submissionTypeOpen}
                onOpen={handleSubmissionTypeOpen}
                onClose={handleSubmissionTypeClose}
                options={SUBMISSION_TYPE_FILTER_OPTIONS}
                value={selectedSubmissionTypeOptions}
                onChange={handleSubmissionTypeChange}
                getOptionLabel={getSubmissionTypeFilterOptionLabel}
                isOptionEqualToValue={(option, value) => {
                  if (option.kind === "toggleAll" || value.kind === "toggleAll") {
                    return option.kind === value.kind;
                  }
                  return option.value === value.value;
                }}
                renderValue={(value, _getItemProps, ownerState) => {
                  if (ownerState.focused) {
                    return null;
                  }
                  const selectedTypes = value.flatMap((option) =>
                    option.kind === "submissionType" ? [option.value] : []
                  );
                  if (selectedTypes.length === ALL_SUBMISSION_TYPES.length) {
                    return `All (${selectedTypes.length})`;
                  }
                  if (selectedTypes.length === 1) {
                    return (
                      <Typography component="span" noWrap>
                        {prettyPrintEnumValue(selectedTypes[0])}
                      </Typography>
                    );
                  }
                  return `${selectedTypes.length} selected`;
                }}
                renderOption={(props, option, { selected }) => {
                  const { key, ...optionProps } = props;
                  if (option.kind === "toggleAll") {
                    return (
                      <li key={key} {...optionProps}>
                        <Box
                          sx={{
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                readOnly
                                checked={isSubmissionTypeFilterAtDefault}
                                indeterminate={
                                  selectedSubmissionTypes.length > 0 &&
                                  !isSubmissionTypeFilterAtDefault
                                }
                              />
                            }
                            label="Show/Hide All"
                            sx={{ margin: 0 }}
                          />
                          {!isSubmissionTypeFilterAtDefault && (
                            <Button
                              size="small"
                              onMouseDown={(event) => event.preventDefault()}
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                handleResetSubmissionTypes();
                              }}
                            >
                              Reset
                            </Button>
                          )}
                        </Box>
                      </li>
                    );
                  }
                  return (
                    <li key={key} {...optionProps}>
                      <Checkbox checked={selected} />
                      {prettyPrintEnumValue(option.value)}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Submission Type"
                    placeholder={selectedSubmissionTypes.length === 0 ? "None" : undefined}
                    variant="standard"
                    inputProps={{
                      ...params.inputProps,
                      autoComplete: "off",
                      "data-lpignore": "true",
                      "data-form-type": "other",
                      "data-1p-ignore": "true",
                    }}
                  />
                )}
                noOptionsText="No submission types found."
                slotProps={{
                  paper: {
                    sx: { minWidth: 280 },
                  },
                }}
              />

              {/* State Filter */}
              <Autocomplete
                multiple
                disableClearable
                disableCloseOnSelect
                openOnFocus
                size="small"
                sx={{
                  width: 140,
                  "& .MuiAutocomplete-inputRoot": {
                    flexWrap: "nowrap",
                  },
                  "& .MuiAutocomplete-input": {
                    minWidth: 0,
                  },
                }}
                open={stateOpen}
                onOpen={handleStateOpen}
                onClose={handleStateClose}
                options={STATE_FILTER_OPTIONS}
                value={selectedStateOptions}
                onChange={handleStatesChange}
                getOptionLabel={getStateFilterOptionLabel}
                isOptionEqualToValue={(option, value) => {
                  if (option.kind === "toggleAll" || value.kind === "toggleAll") {
                    return option.kind === value.kind;
                  }
                  return option.value === value.value;
                }}
                renderValue={(value, _getItemProps, ownerState) => {
                  if (ownerState.focused) {
                    return null;
                  }
                  const states = value.flatMap((option) =>
                    option.kind === "state" ? [option.value] : []
                  );
                  let summary: string;
                  if (states.length === 0) {
                    summary = "None";
                  } else if (states.length === US_STATES.length) {
                    summary = `All (${states.length})`;
                  } else if (states.length === 1) {
                    summary = states[0];
                  } else {
                    summary = `${states.length} selected`;
                  }
                  return (
                    <Typography component="span" noWrap>
                      {summary}
                    </Typography>
                  );
                }}
                renderOption={(props, option, { selected }) => {
                  const { key, ...optionProps } = props;
                  if (option.kind === "toggleAll") {
                    return (
                      <li key={key} {...optionProps}>
                        <Box
                          sx={{
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                readOnly
                                checked={isStateFilterAtDefault}
                                indeterminate={selectedStates.length > 0 && !isStateFilterAtDefault}
                              />
                            }
                            label="Show/Hide All"
                            sx={{ margin: 0 }}
                          />
                          {!isStateFilterAtDefault && (
                            <Button
                              size="small"
                              onMouseDown={(event) => event.preventDefault()}
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                handleResetStates();
                              }}
                            >
                              Reset
                            </Button>
                          )}
                        </Box>
                      </li>
                    );
                  }
                  return (
                    <li key={key} {...optionProps}>
                      <Checkbox checked={selected} />
                      {getStateName(option.value)}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="State/Territory"
                    placeholder={selectedStates.length === 0 ? "None" : undefined}
                    variant="standard"
                    inputProps={{
                      ...params.inputProps,
                      autoComplete: "off",
                      "data-lpignore": "true",
                      "data-form-type": "other",
                      "data-1p-ignore": "true",
                    }}
                  />
                )}
                noOptionsText="No states or territories found."
                slotProps={{
                  paper: {
                    sx: { minWidth: 280, maxHeight: 400 },
                  },
                }}
              />

              {/* Due Date Range Filter */}
              <LocalizationProvider dateAdapter={AdapterLuxon}>
                <DateRangePicker
                  value={[dueDateStart, dueDateEnd]}
                  onChange={handleDueDateRangeChange}
                  localeText={{ start: "Due Start", end: "Due End" }}
                  slotProps={{
                    shortcuts: {
                      items: datePickerShortcuts,
                    },
                    textField: {
                      variant: "standard",
                      size: "small",
                      sx: { width: 105 },
                      inputProps: {
                        autoComplete: "off",
                        "data-lpignore": "true",
                        "data-form-type": "other",
                        "data-1p-ignore": "true",
                      },
                    },
                  }}
                  slots={{
                    field: MultiInputDateRangeField,
                    shortcuts: DateShortcutChips,
                  }}
                />
              </LocalizationProvider>

              <Stack
                direction="row"
                gap={1}
                alignItems="center"
                sx={{ alignSelf: "flex-end", mb: 0.5 }}
              >
                <FormControl variant="standard" size="small" sx={{ minWidth: 180 }}>
                  <InputLabel id="staff-view-by-label">View by</InputLabel>
                  <Select<StaffViewByValue>
                    labelId="staff-view-by-label"
                    value={staffViewByValue}
                    onChange={handleStaffViewByChange}
                    renderValue={(v) => {
                      switch (v) {
                        case "assignedToMe":
                          return "Assigned to me";
                        case "queuedForMe":
                          return "In my queue";
                        case "needsMyAttention":
                          return "Needs my attention";
                        case "peerReviewQueue":
                          return "Peer review queue";
                        default:
                          return "All";
                      }
                    }}
                  >
                    <MenuItem value="all">
                      <Radio
                        checked={staffViewByValue === "all"}
                        size="small"
                        sx={{ p: 0, mr: 1 }}
                      />
                      All
                    </MenuItem>
                    <MenuItem value="assignedToMe">
                      <Radio
                        checked={staffViewByValue === "assignedToMe"}
                        size="small"
                        sx={{ p: 0, mr: 1 }}
                      />
                      Assigned to me
                    </MenuItem>
                    <MenuItem value="queuedForMe">
                      <Radio
                        checked={staffViewByValue === "queuedForMe"}
                        size="small"
                        sx={{ p: 0, mr: 1 }}
                      />
                      In my queue
                    </MenuItem>
                    <MenuItem value="needsMyAttention">
                      <Radio
                        checked={staffViewByValue === "needsMyAttention"}
                        size="small"
                        sx={{ p: 0, mr: 1 }}
                      />
                      Needs my attention
                    </MenuItem>
                    <MenuItem value="peerReviewQueue">
                      <Radio
                        checked={staffViewByValue === "peerReviewQueue"}
                        size="small"
                        sx={{ p: 0, mr: 1 }}
                      />
                      Peer review queue
                    </MenuItem>
                  </Select>
                </FormControl>
                <Tooltip
                  title={
                    isOverdueToggleRedundant
                      ? `Disabled — your Due Start (${dueDateStart?.toFormat("MMM d, yyyy")}) is already on or before the overdue lookback floor (${overdueCutoffDate}), so the toggle has no effect.`
                      : "Extends the lower bound of the date range backwards to today − 3 months so already-overdue items still appear. Never narrows the range you set in Due Start. Items older than 3 months are excluded to focus on actionable work."
                  }
                >
                  <Stack direction="row" alignItems="center" gap={0.5}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={includeOverdue && !isOverdueToggleRedundant}
                          onChange={handleIncludeOverdueChange}
                          color="primary"
                          size="small"
                          disabled={isOverdueToggleRedundant}
                        />
                      }
                      label="Include Overdue"
                      sx={{
                        m: 0,
                        "& .MuiFormControlLabel-label": {
                          fontSize: "0.875rem",
                          color: isOverdueToggleRedundant ? "text.disabled" : "text.primary",
                        },
                      }}
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ whiteSpace: "nowrap" }}
                    >
                      (since {overdueCutoffDate})
                    </Typography>
                  </Stack>
                </Tooltip>
                <Button
                  onClick={handleResetFilters}
                  variant="text"
                  size="small"
                  sx={{
                    visibility: areFiltersAtDefault ? "hidden" : "visible",
                  }}
                >
                  Reset
                </Button>
              </Stack>
            </Stack>

            <Stack direction="row" gap={0.5} alignItems="center">
              <Tooltip title="Export current filters to CSV (includes facility UUID)">
                <span>
                  <Button
                    onClick={handleExportCsv}
                    disabled={exportLoadingOrPolling}
                    variant="text"
                    size="small"
                    startIcon={<FileDownload />}
                  >
                    Export CSV
                  </Button>
                </span>
              </Tooltip>
              <Button onClick={handleToggle} variant="text" size="small">
                {statsOpen ? "Hide Stats" : "Show Stats"}
              </Button>
              <Button
                component={RouterLink}
                to="/staff/fulfillment/waste/manifest-review"
                variant="text"
                size="small"
              >
                Manifest Review
              </Button>
            </Stack>
          </Stack>
        </Stack>
      )}

      {isOverviewRoute && (
        <Collapse in={statsOpen}>
          <Stack paddingX={3} paddingY={2}>
            <SummaryOverview defaultSearch={defaultSearch} />
          </Stack>
        </Collapse>
      )}

      {isOverviewRoute && (
        <Stack paddingX={3} paddingBottom={3}>
          <GridSection defaultSearch={defaultSearch} />
        </Stack>
      )}

      <Outlet />
    </Stack>
  );
}

/**
 * Provider order matches the customer-side WasteSubmissions pattern:
 * 1. StatsProvider - manages collapse state
 * 2. OmnisearchProvider - SINGLE useOmnisearchDatagrid instance shared by
 *    filter bar, charts, and grid via context. Eliminates the dual-instance
 *    oscillation bug where two useOmnisearchInput hooks fought over the URL.
 */
export function WasteFulfillmentDashboard() {
  // Manifest Review renders a self-contained OmnisearchDataGrid with its own
  // columns and omnisearch state. It must NOT mount inside the submissions
  // OmnisearchProvider below: OmnisearchDataDisplay prefers a surrounding
  // provider's grid state (columns, filter keys, omnisearch) over its own
  // props, which would render submission columns over manifest rows and let
  // the two grids fight over the `omnisearch` URL param. Render it bare so it
  // uses its own internal state.
  const isManifestReview = useMatch("/staff/fulfillment/waste/manifest-review");
  if (isManifestReview) {
    return <Outlet />;
  }

  return (
    <StatsProvider>
      <OmnisearchProvider
        persistenceKey="waste-submissions-staff-v2"
        columns={COLUMNS}
        isURLDriven
        persistOmnisearch
        initialSortModel={[{ field: "deadline", sort: "asc" }]}
        initialPageSize={50}
        additionalFilters={ADDITIONAL_FILTER_COLUMNS}
        excludeFilters={EXCLUDE_FILTER_COLUMNS}
      >
        <WasteFulfillmentDashboardContent />
      </OmnisearchProvider>
    </StatsProvider>
  );
}
